/**
 * The fields of an import, in groups a person recognises: what the row is,
 * when, where, the round, results, and one group per side or list (`home.*`,
 * `ranked[].*`).
 *
 * Worked out from the keys and labels the server gives, with words that mean
 * the same in every sport, so a new import gets sensible groups without anyone
 * listing them here.
 */

import { isMapped, type Settings } from "@/lib/integrations/settings";

export type GroupableField = { key: string; label: string; required: boolean; many?: boolean };

export type FieldGroup<F extends GroupableField = GroupableField> = {
  key: string;
  title: string;
  /** A list: every entry of it gets these fields. */
  list: boolean;
  fields: F[];
};

/** Checked in this order; the first topic with a matching word wins. */
const TOPICS: { key: string; title: string; words: RegExp }[] = [
  {
    key: "when",
    title: "When and status",
    words: /^(start|starts|end|ends|date|day|time|kickoff|scheduled|planned|status|live|state|finished)$/,
  },
  { key: "where", title: "Where", words: /^(venue|location|city|stadium|arena|court|track|ground|pitch|site)$/ },
  {
    key: "round",
    title: "Sport, event and round",
    words: /^(sport|discipline|competition|event|phase|round|stage|unit|group|pool|heat|leg|match|medal|session)$/,
  },
  {
    key: "result",
    title: "Results",
    words: /^(score|scores|scoreline|result|results|winner|won|rank|place|position|points|gold|silver|bronze|total|medals|irm)$/,
  },
  { key: "identity", title: "What it is", words: /^(id|external|key|code|name|noc|team|gender|size|type|kind|alt|title|short)$/ },
];

const OTHER = { key: "other", title: "Other fields" };

/** `statusName` → ["status", "name"]; `ranked[].id` → ["ranked", "id"]. */
export function keyWords(text: string): string[] {
  return text
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function topicOf(field: GroupableField): { key: string; title: string } {
  for (const words of [keyWords(field.key), keyWords(field.label)]) {
    for (const topic of TOPICS) if (words.some((word) => topic.words.test(word))) return topic;
  }
  return OTHER;
}

/** The part before the first `.` or `[]`: `home` of `home.id`. */
function headOf(key: string): string | null {
  const match = /^([^.[\]]+)(\[\]|\.)/.exec(key);
  return match ? match[1] : null;
}

/** "Home" of "Home: their id". */
function prefixOf(label: string): string | null {
  const match = /^(.+?):\s/.exec(label);
  return match ? match[1].trim() : null;
}

function humanise(text: string): string {
  const words = keyWords(text).join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Fields in groups, in the order the import lists them. Two or more fields
 * under one key (`home.id`, `home.name`) are a group of their own; the rest go
 * by topic. Required fields come first inside each group.
 */
export function groupFields<F extends GroupableField>(fields: F[]): FieldGroup<F>[] {
  const heads = new Map<string, number>();
  for (const field of fields) {
    const head = headOf(field.key);
    if (head) heads.set(head, (heads.get(head) ?? 0) + 1);
  }
  const groups = new Map<string, FieldGroup<F>>();
  for (const field of fields) {
    const head = headOf(field.key);
    const part = head !== null && (heads.get(head) ?? 0) >= 2;
    const topic = part ? { key: `part:${head}`, title: "" } : topicOf(field);
    let group = groups.get(topic.key);
    if (!group) {
      group = { key: topic.key, title: topic.title, list: part && field.key.startsWith(`${head}[]`), fields: [] };
      groups.set(topic.key, group);
    }
    group.fields.push(field);
  }
  return [...groups.values()].map((group) => {
    let title = group.title;
    if (group.key.startsWith("part:")) {
      const prefixes = new Set(group.fields.map((field) => prefixOf(field.label)));
      const [only] = [...prefixes];
      title = prefixes.size === 1 && only ? only : humanise(group.key.slice(5));
    }
    return {
      ...group,
      title,
      fields: [...group.fields.filter((field) => field.required), ...group.fields.filter((field) => !field.required)],
    };
  });
}

/** A field's label inside its group, without the group's name in front: "Their id" in Home. */
export function labelInGroup(label: string, group: Pick<FieldGroup, "title">): string {
  const prefix = `${group.title}:`;
  if (!label.startsWith(prefix)) return label;
  const rest = label.slice(prefix.length).trim();
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : label;
}

export type GroupCount = { total: number; mapped: number; required: number; requiredMissing: number };

/** How much of a group is mapped. A required list field is not counted as missing, as on the Fields stage. */
export function groupCount(group: Pick<FieldGroup, "fields">, settings: Pick<Settings, "fields">): GroupCount {
  let mapped = 0;
  let required = 0;
  let requiredMissing = 0;
  for (const field of group.fields) {
    const done = isMapped(settings.fields[field.key]);
    if (done) mapped += 1;
    if (field.required && !field.many) {
      required += 1;
      if (!done) requiredMissing += 1;
    }
  }
  return { total: group.fields.length, mapped, required, requiredMissing };
}
